import { createContext, useContext, useState } from "react";
import { jsx as _jsx } from "react/jsx-runtime";
import {
	doRequestWithProductSigning,
	doRequestWithSigning,
	type SigningRequiredResponse,
} from "../services/signing";
import { useLogin } from "./login";

export type ProductBankIdSignStatus = "complete" | "pending" | "failed";

export type AuthStatus = "pending" | "successful";

export type BankIdSigningHintCode =
	| "OutstandingTransaction"
	| "NoClient"
	| "Started"
	| "UserMrtd"
	| "UserCallConfirm"
	| "UserSign"
	| "ExpiredTransaction"
	| "CertificateErr"
	| "UserCancel"
	| "Cancelled"
	| "StartFailed";

export type ProductSigningStatus = {
	autoStartToken: string | null;
	hintCode: BankIdSigningHintCode | null;
	qrCode: string | null;
	scope: "signing" | null;
	status: ProductBankIdSignStatus;
};

type SigningContextType = {
	qrCode: string | null;
	bankIdToken: string | null;
	isSigning: boolean;
	isCancelling: boolean;
	onCancel: (() => void) | null;
	requestWithProductSigning: (
		initRequest: (applicationId: string) => Promise<ProductSigningStatus>,
		getStatusRequest: (applicationId: string) => Promise<ProductSigningStatus>,
		cancelRequest: (applicationId: string) => Promise<unknown>,
	) => (applicationId: string) => Promise<void>;
	requestWithSigning: <T, A>(
		request: (args: A) => Promise<SigningRequiredResponse<T>>,
	) => (args: A) => Promise<T>;
	onExtendTime: () => void;
	startTime: number;
	maxTime: number;
};

const SigningContext = createContext<SigningContextType | undefined>(undefined);

export const SigningProvider = ({
	maxTime = 300 * 1000,
	initialValues,
	children,
}: {
	maxTime?: number;
	initialValues?: Omit<
		SigningContextType,
		"requestWithProductSigning" | "requestWithSigning" | "maxTime"
	>;
	children: React.ReactNode;
}) => {
	const [qrCode, setQrCode] = useState<string | null>(initialValues?.qrCode ?? null);
	const [bankIdToken, setBankIdToken] = useState<string | null>(initialValues?.bankIdToken ?? null);
	const [isSigning, setIsSigning] = useState(!!initialValues?.isSigning);
	const [onCancel, setOnCancel] = useState<(() => void) | null>(initialValues?.onCancel ?? null);
	const [onExtendTime, setOnExtendTime] = useState<(() => void) | null>(
		initialValues?.onExtendTime ?? null,
	);
	const [startTime, setStartTime] = useState(initialValues?.startTime ?? 0);

	const [isCancelling, setIsCancelling] = useState(!!initialValues?.isCancelling);
	const resetSigningState = () => {
		setQrCode(null);
		setIsSigning(false);
		setBankIdToken(null);
		setIsCancelling(false);
	};

	// Best way to get this? Can we depend on LoginProvider?
	const { baseUrl, appVersion } = useLogin();

	const requestWithSigning = <T, A>(request: (args: A) => Promise<SigningRequiredResponse<T>>) => {
		return doRequestWithSigning({
			baseUrl,
			appVersion,
			maxTime,
			request,
			setIsSigning,
			setBankIdToken,
			setOnExtendTime,
			setQrCode,
			resetSigningState,
			setStartTime,
			setOnCancel,
		});
	};

	const requestWithProductSigning = (
		initRequest: (applicationId: string) => Promise<ProductSigningStatus>,
		getStatusRequest: (applicationId: string) => Promise<ProductSigningStatus>,
		cancelRequest: (applicationId: string) => Promise<void>,
	) => {
		return doRequestWithProductSigning({
			maxTime,
			initRequest,
			getStatusRequest,
			cancelRequest,
			setIsSigning,
			setBankIdToken,
			setQrCode,
			resetSigningState,
			setStartTime,
			setOnExtendTime,
			setOnCancel,
		});
	};

	// TODO: use JSX/TSX instead. This is being used because PostCSS complains that it cant find this file if it's called .tsx.
	return _jsx(SigningContext.Provider, {
		value: {
			isSigning,
			onCancel,
			qrCode,
			bankIdToken,
			isCancelling,
			requestWithProductSigning,
			requestWithSigning,
			onExtendTime,
			startTime,
			maxTime,
		},
		children: children,
	});
};

export const useSigning = () => {
	const context = useContext(SigningContext);
	if (!context) throw new Error("useSigningContext must be used inside a SigningProvider");

	return context;
};
